import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  DatePicker,
  Collapse,
  Empty,
  Segmented,
  Typography,
} from "antd";
import { getPatientMonitoringChartsApi } from "../../../services/patient.services";
import VitalsTable from "./VitalsTable";
import GrbsTable from "./GrbsTable";
import UrinaryCatheter from "./UrinaryCatheter";
import Nebulization from "./Nebulization";
import IVFluids from "./IVFluids";
import BloodProducts from "./BloodProducts";
import ParentalNutrition from "./ParentalNutrition";
import RespiratoryCare from "./RespiratoryCare/RespiratoryCare";
import OpenWoundCare from "./OpenWoundCare/OpenWoundCare";
import PreventiveCare from "./PreventiveCare/PreventiveCare";
import EyeCare from "./EyeCare/EyeCare";
import FeedingTube from "./FeedingTube";
import PressureSore from "./PressureSore/PressureSore";
import DailyRoutine from "./DailyRoutine/DailyRoutine";
import OstomyCare from "./OstomyCare";
import SpecialistConsultation from "./SpecialistConsultation";
import Medications from "./Medications/Medications";
import Diet from "./Diet/Diet";
import dayjs from "dayjs";
import PositionChange from "./PositionChange";
import IoData from "./IoData";
import "./MonitoringCharts.css";
import useIsMobile from "../../../hooks/useIsMobile";
import useQuery from "../../../hooks/useQuery";

const { RangePicker } = DatePicker;
const dateFormat = "YYYY-MM-DD";
const { Text } = Typography;

const hasSectionData = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (value && typeof value === "object") {
    return Object.keys(value).length > 0;
  }
  return Boolean(value);
};

const sectionRecordCount = (value) => {
  if (Array.isArray(value)) {
    return value.length;
  }
  if (value && typeof value === "object") {
    return Object.keys(value).length;
  }
  return 0;
};

/** "IO Monitoring" -> "io-monitoring", so a section can be named in the URL. */
const sectionSlug = (label) =>
  String(label || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-");

const MonitoringCharts = ({ patient, patientData }) => {
  const params = useQuery();
  // Set when arriving from a summary card, so we open that card's section
  // instead of falling back to the first one with data.
  const requestedSection = params?.chart || null;
  const [data, setData] = useState(null);
  const [activeKeys, setActiveKeys] = useState([]);
  const [sectionFilter, setSectionFilter] = useState("all");
  const isMobile = useIsMobile();
  const [hasInitializedPanels, setHasInitializedPanels] = useState(false);

  const requestedRange = useMemo(() => {
    const from = params?.chart_from ? dayjs(params.chart_from) : null;
    const to = params?.chart_to ? dayjs(params.chart_to) : null;
    if (!from?.isValid() || !to?.isValid() || from.isAfter(to)) return null;
    return { start_date: from.startOf("day"), end_date: to.endOf("day") };
  }, [params?.chart_from, params?.chart_to]);

  const [dateFilter, setDateFilter] = useState(
    requestedRange || {
      start_date: dayjs().subtract(1, "d").startOf("day"),
      end_date: dayjs().endOf("day"),
    }
  );
  const [rangeDraft, setRangeDraft] = useState([
    dayjs().subtract(1, "d").startOf("day"),
    dayjs().endOf("day"),
  ]);

  const admissionDate = patientData?.admission?.admission_date
    ? dayjs(patientData.admission.admission_date).startOf("day")
    : null;
  const dischargeDate = patientData?.admission?.discharge_date
    ? dayjs(patientData.admission.discharge_date).endOf("day")
    : null;
  const maxSelectableDate = dischargeDate || dayjs().endOf("day");

  useEffect(() => {
    if (!patient || !dateFilter?.start_date || !dateFilter?.end_date) {
      return;
    }

    getPatientMonitoringChartsApi(
      patient,
      `start_date=${dateFilter.start_date.format(
        dateFormat
      )}&end_date=${dateFilter.end_date.format(dateFormat)}`
    )
      .then((res) => {
        setData(res?.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [patient, dateFilter]);

  useEffect(() => {
    setRangeDraft([dateFilter.start_date, dateFilter.end_date]);
  }, [dateFilter.start_date, dateFilter.end_date]);

  const disableOutOfRangeDates = (current) => {
    if (!current) return false;
    if (admissionDate && current.isBefore(admissionDate, "day")) {
      return true;
    }
    return current.isAfter(maxSelectableDate, "day");
  };

  const applyRangePreset = (range) => {
    const nextRange = [range[0].startOf("day"), range[1].endOf("day")];
    setRangeDraft(nextRange);
    setDateFilter({
      start_date: nextRange[0],
      end_date: nextRange[1],
    });
  };

  const rangePresets = useMemo(() => {
    const todayStart = maxSelectableDate.startOf("day");
    const yesterdayStart = todayStart.subtract(1, "day");
    const sevenDaysStart = todayStart.subtract(6, "day");

    const safeYesterdayStart =
      admissionDate && yesterdayStart.isBefore(admissionDate, "day")
        ? admissionDate
        : yesterdayStart;

    const safeSevenDaysStart =
      admissionDate && sevenDaysStart.isBefore(admissionDate, "day")
        ? admissionDate
        : sevenDaysStart;

    return [
      { label: "Today", value: [todayStart, maxSelectableDate] },
      {
        label: "Yesterday",
        value: [safeYesterdayStart, safeYesterdayStart.endOf("day")],
      },
      {
        label: "Last 7 Days",
        value: [safeSevenDaysStart, maxSelectableDate],
      },
    ];
  }, [admissionDate, maxSelectableDate]);

  const chartData = data?.data;

  const sections = useMemo(
    () => [
      {
        key: "1",
        label: "Vitals",
        source: chartData?.vitals,
        content: <VitalsTable data={chartData?.vitals} />,
      },
      {
        key: "2",
        label: "GRBS",
        source: chartData?.grbs,
        content: <GrbsTable data={chartData?.grbs} />,
      },
      {
        key: "3",
        label: "Medication",
        source: chartData?.medications,
        content: <Medications data={chartData?.medications} />,
      },
      {
        key: "4",
        label: "Nebulization",
        source: chartData?.nebulization,
        content: <Nebulization data={chartData?.nebulization} />,
      },
      {
        key: "5",
        label: "IV Fluids",
        source: chartData?.iv_fluids,
        content: <IVFluids data={chartData?.iv_fluids} />,
      },
      {
        key: "6",
        label: "Blood Products",
        source: chartData?.blood_products,
        content: <BloodProducts data={chartData?.blood_products} />,
      },
      {
        key: "7",
        label: "Parental Nutrition",
        source: chartData?.parental_nutrition,
        content: <ParentalNutrition data={chartData?.parental_nutrition} />,
      },
      {
        key: "8",
        label: "Respiratory Care",
        source:
          chartData?.respiratory_care ||
          chartData?.respiratory_care_respiratory_support ||
          chartData?.respiratory_care_tracheostomy_care ||
          chartData?.respiratory_care_chest_physiotherapy,
        content: <RespiratoryCare data={data} />,
      },
      {
        key: "9",
        label: "Urinary Catheter",
        source: chartData?.urinary_catheter_care,
        content: <UrinaryCatheter data={chartData?.urinary_catheter_care} />,
      },
      {
        key: "10",
        label: "Wound Care",
        source: chartData?.open_wound_care,
        content: <OpenWoundCare data={chartData?.open_wound_care} />,
      },
      {
        key: "11",
        label: "Preventive Care",
        source:
          chartData?.preventive_care ||
          chartData?.preventive_care_central_line_care ||
          chartData?.preventive_care_thromboprophylaxis ||
          chartData?.preventive_care_pressure_sore_care,
        content: <PreventiveCare data={data} />,
      },
      {
        key: "12",
        label: "Eye Care",
        source:
          chartData?.eye_care ||
          chartData?.eye_care_saline_wash ||
          chartData?.eye_care_eye_drops ||
          chartData?.eye_care_eye_gel_ointment ||
          chartData?.eye_care_trasorrhaphy ||
          chartData?.eye_care_eye_patching_guard,
        content: <EyeCare data={data} />,
      },
      {
        key: "13",
        label: "Feeding Tube",
        source: chartData?.feeding_tube,
        content: <FeedingTube data={chartData?.feeding_tube} />,
      },
      {
        key: "14",
        label: "Pressure Sore Care",
        source: chartData?.pressure_sore_care,
        content: <PressureSore data={chartData?.pressure_sore_care} />,
      },
      {
        key: "15",
        label: "Ostomy Care",
        source: chartData?.ostomy_care,
        content: <OstomyCare data={chartData?.ostomy_care} />,
      },
      {
        key: "20",
        label: "Specialist Consultation",
        source: chartData?.specialist_consultation,
        content: (
          <SpecialistConsultation data={chartData?.specialist_consultation} />
        ),
      },
      {
        key: "16",
        label: "Position Change",
        source: chartData?.position_change_schedule,
        content: <PositionChange data={chartData?.position_change_schedule} />,
      },
      {
        key: "17",
        label: "IO Monitoring",
        source: chartData?.io_data,
        content: <IoData data={chartData?.io_data} />,
      },
      {
        key: "18",
        label: "Diet",
        source: chartData?.diet,
        content: <Diet data={chartData?.diet} />,
      },
      {
        key: "19",
        label: "Daily Routine",
        source: chartData?.daily_routine,
        content: <DailyRoutine data={chartData?.daily_routine} />,
      },
    ],
    [chartData, data]
  );

  const sectionsWithMeta = useMemo(
    () =>
      sections.map((section) => ({
        ...section,
        hasData: hasSectionData(section.source),
        recordCount: sectionRecordCount(section.source),
      })),
    [sections]
  );

  const visibleSections =
    sectionFilter === "with_data"
      ? sectionsWithMeta.filter((section) => section.hasData)
      : sectionsWithMeta;

  useEffect(() => {
    setActiveKeys((prev) => {
      const previous = Array.isArray(prev) ? prev : prev ? [prev] : [];
      const retained = previous.filter((key) =>
        visibleSections.some((section) => section.key === key)
      );
      if (retained.length > 0) {
        return retained;
      }
      // If user intentionally closed all panels, keep them closed.
      if (hasInitializedPanels && previous.length === 0) {
        return [];
      }

      const requested = requestedSection
        ? visibleSections.find(
            (section) => sectionSlug(section.label) === requestedSection
          )?.key
        : null;

      const firstAvailable =
        requested ||
        visibleSections.find((section) => section.hasData)?.key ||
        visibleSections[0]?.key;

      // Auto-open only once on initial load, or when previously opened panels disappear.
      if (!hasInitializedPanels) {
        setHasInitializedPanels(true);
      }
      return firstAvailable ? [firstAvailable] : [];
    });
  }, [visibleSections, hasInitializedPanels, requestedSection]);

  const didScrollToRequested = useRef(false);
  useEffect(() => {
    if (!requestedSection || didScrollToRequested.current) return;
    const target = visibleSections.find(
      (section) => sectionSlug(section.label) === requestedSection
    );
    if (!target || !activeKeys.includes(target.key)) return;

    const el = document.getElementById(`monitoring-section-${target.key}`);
    if (!el) return;
    didScrollToRequested.current = true;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [requestedSection, visibleSections, activeKeys]);

  const handleActiveKeysChange = (keys) => {
    setActiveKeys(Array.isArray(keys) ? keys : keys ? [keys] : []);
  };

  const sectionsWithData = sectionsWithMeta.filter((section) => section.hasData);

  return (
    <div className="monitoring-charts-container">
      <div className="monitoring-charts-toolbar">
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={12}>
            <Text className="monitoring-charts-summary">
              {sectionsWithData.length} of {sectionsWithMeta.length} sections with records
            </Text>
          </Col>
          <Col xs={24} md={12} className="monitoring-charts-controls">
            <Segmented
              size="middle"
              value={sectionFilter}
              onChange={setSectionFilter}
              options={[
                { label: "All Sections", value: "all" },
                { label: "With Data", value: "with_data" },
              ]}
            />
            <RangePicker
              allowClear={false}
              disabledDate={disableOutOfRangeDates}
              presets={isMobile ? undefined : rangePresets}
              style={isMobile ? { width: "100%" } : undefined}
              getPopupContainer={isMobile ? () => document.body : undefined}
              placement={isMobile ? "bottomLeft" : undefined}
              popupClassName={
                isMobile ? "curengo-mobile-picker monitoring-charts-mobile-range" : undefined
              }
              popupStyle={
                isMobile
                  ? { maxWidth: "calc(100vw - 16px)", width: "calc(100vw - 16px)" }
                  : undefined
              }
              panelRender={
                isMobile
                  ? (originPanel) => (
                      <div className="monitoring-charts-range-mobile-panel">
                        <div className="monitoring-charts-range-mobile-presets">
                          {rangePresets.map((preset) => (
                            <button
                              key={preset.label}
                              type="button"
                              className="monitoring-charts-range-preset-btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                applyRangePreset(preset.value);
                              }}
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                        <div className="monitoring-charts-range-mobile-calendar">
                          {originPanel}
                        </div>
                      </div>
                    )
                  : undefined
              }
              onCalendarChange={(range) => {
                if (!range) return;
                setRangeDraft(range);
              }}
              onChange={(range) => {
                if (!range?.[0] || !range?.[1]) return;
                setDateFilter({
                  start_date: range[0].startOf("day"),
                  end_date: range[1].endOf("day"),
                });
                setRangeDraft(range);
              }}
              value={rangeDraft}
            />
          </Col>
        </Row>
      </div>

      <div className="monitoring-charts-list">
        <Row>
          <Col span={24}>
            {visibleSections.length === 0 ? (
              <div className="monitoring-charts-empty">
                <Empty description="No sections with records in this date range" />
              </div>
            ) : (
              <Collapse
                expandIconPosition="end"
                className="custom-collapse monitoring-collapse"
                activeKey={activeKeys}
                onChange={handleActiveKeysChange}
              >
                {visibleSections.map((section) => (
                  <Collapse.Panel
                    key={section.key}
                    header={
                      <div
                        className="monitoring-panel-header"
                        id={`monitoring-section-${section.key}`}
                      >
                        <span className="monitoring-panel-title">{section.label}</span>
                        <span
                          className={`monitoring-panel-pill ${
                            section.hasData ? "monitoring-panel-pill-active" : ""
                          }`}
                        >
                          {section.hasData
                            ? `${section.recordCount} ${
                                section.recordCount === 1 ? "record" : "records"
                              }`
                            : "No records"}
                        </span>
                      </div>
                    }
                  >
                    {section.content}
                  </Collapse.Panel>
                ))}
              </Collapse>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default MonitoringCharts;
